// ===== 1. AI Recommendation Service =====
// src/services/aiSubcodeRecommendationService.ts

import { httpRequest } from "../httpRequest";

export interface SubcodeRecommendation {
  subcodeType: string;
  required: boolean;
  confidence: number;
  reason: string;
  priority: number;
}

export interface AIRecommendationResponse {
  recommendations: SubcodeRecommendation[];
  overallConfidence: number;
  warningMessages: string[];
  infoMessages: string[];
}

export class AISubcodeRecommendationService {

  async getSubcodeRecommendations(formData: any): Promise<AIRecommendationResponse> {
    try {
      const response1 = await httpRequest('POST', '/construction/AI/generateAIRecommendSubCodes', { formData })

      if (response1?.status !== 'success') {
        throw new Error(`API request failed: ${response1.message}`);
      }

      return this.validateAndSanitize(response1?.data);

    } catch (error) {
      console.error('AI Recommendation Error:', error);
      return this.getFallbackRecommendations(formData);
    }
  }

  private validateAndSanitize(recommendations: any): AIRecommendationResponse {
    if (!recommendations.recommendations || !Array.isArray(recommendations.recommendations)) {
      throw new Error('Invalid AI response format');
    }

    recommendations.recommendations = recommendations.recommendations.filter((rec: any) => {
      return rec.subcodeType &&
        typeof rec.required === 'boolean' &&
        rec.confidence >= 0 && rec.confidence <= 1;
    });

    return {
      recommendations: recommendations.recommendations,
      overallConfidence: recommendations.overallConfidence || 0.7,
      warningMessages: recommendations.warningMessages || [],
      infoMessages: recommendations.infoMessages || []
    };
  }

  private getFallbackRecommendations(formData: any): AIRecommendationResponse {
    const fallback: AIRecommendationResponse = {
      recommendations: [
        {
          subcodeType: 'Building',
          required: true,
          confidence: 1.0,
          reason: 'Building permit typically required (AI unavailable)',
          priority: 1
        }
      ],
      overallConfidence: 0.7,
      warningMessages: ['AI recommendations unavailable - please consult permit office'],
      infoMessages: []
    };

    if (formData.DescribeUse?.toLowerCase().includes('electrical')) {
      fallback.recommendations.push({
        subcodeType: 'Electrical',
        required: true,
        confidence: 0.8,
        reason: 'Electrical work detected in description',
        priority: 2
      });
    }

    if (formData.DescribeUse?.toLowerCase().includes('plumbing') ||
      formData.DescribeUse?.toLowerCase().includes('bathroom') ||
      formData.DescribeUse?.toLowerCase().includes('kitchen')) {
      fallback.recommendations.push({
        subcodeType: 'Plumbing',
        required: true,
        confidence: 0.8,
        reason: 'Plumbing work detected in description',
        priority: 2
      });
    }

    return fallback;
  }
}